import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';

import type { PaymentMethodProvider } from '#src/__generated__/globalTypes.ts';
import {
  Level,
  useSnackNotificationContext,
} from '#src/contexts/snackNotification/index.ts';
import { formatGqlErrors } from '#src/lib/gql/index.ts';

import type {
  DetachPaymentMethodMutation,
  DetachPaymentMethodMutationVariables,
} from './__generated__/useDetachPaymentMethod.graphql.ts';

const DETACH_PAYMENT_METHOD_MUTATION = gql`
  mutation DetachPaymentMethodMutation($input: detachPaymentMethodInput!) {
    detachPaymentMethod(input: $input) {
      currentUser {
        slug
        stripePaymentMethods: paymentMethods(provider: STRIPE) {
          id
        }
        mangopayPaymentMethods: paymentMethods(provider: MANGOPAY) {
          id
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  DetachPaymentMethodMutation,
  DetachPaymentMethodMutationVariables
>;

export const useDetachPaymentMethod = () => {
  const [detachPaymentMethod, { loading }] = useMutation(
    DETACH_PAYMENT_METHOD_MUTATION
  );
  const { showNotification } = useSnackNotificationContext();

  const detachPaymentMethodCallback = async (
    paymentMethod: string,
    provider: PaymentMethodProvider
  ) => {
    const result = await detachPaymentMethod({
      variables: { input: { paymentMethod, provider } },
    });

    const errors = result.data?.detachPaymentMethod?.errors || [];

    if (errors.length) {
      showNotification(
        'errors',
        { errors: formatGqlErrors(errors) },
        { level: Level.WARN }
      );
      return errors;
    }
    showNotification('paymentMethodDetached');
    return null;
  };
  return {
    detachPaymentMethod: detachPaymentMethodCallback,
    loading,
  };
};
